import { FloatingTextConfig } from '../types/EffectTypes';
import { EnhancedParticleManager } from '../EnhancedParticleManager';
import { resolveFloatingTextColor } from '../../../shared/config/combat-feedback.config';
import { getMotionPrefs } from '../../systems/accessibility/motionPrefs';
import { spawnFloatingDamageText } from '../../rendering/utils/FloatingDamageText';
import {
  FCT_PRESET_STYLE_VERBS,
  resolveFctStyle,
} from '../../../config/ui/fctStyle.config';
import type { FctMotionVerb } from '../../../config/ui/fctStyle.config';

/** Parse a CSS hex color string '#RRGGBB' into a 0xRRGGBB integer. */
function parseHexColor(hex: string): number {
  const trimmed = hex.replace(/^#/, '');
  const n = parseInt(trimmed, 16);
  return Number.isFinite(n) ? n : 0xffffff;
}

/**
 * Phase 2 additive knobs — beat callers steer the executor (verb, gating,
 * slots) without widening the shared EffectTypes contract. All optional;
 * legacy callers keep passing plain FloatingTextConfig.
 */
export interface StyledFloatingTextConfig extends FloatingTextConfig {
  /** Named motion verb. Undefined ⇒ resolved from `style` via FCT_PRESET_STYLE_VERBS. */
  verb?: FctMotionVerb;
  /** Weight tier 0–3 for the executor's cap/degradation gates. Default 1. */
  tier?: number;
  /** Per-target slot-ring key — consecutive beats on one target fan laterally. */
  targetKey?: string;
  /** Never dropped by cap/degrade gates. Beats default to true (spec §3.2). */
  alwaysRender?: boolean;
  /** Companion label riding another spawn — no slot alloc, no cap accounting. */
  companion?: boolean;
}

/**
 * FloatingTextManager — beat-text spawner for one-shot combat texts
 * (lifesteal, mana, interrupts, ability names, XP, effect-def texts).
 *
 * FCT choreography Phase 2 (spec §5 integration 11): this manager no longer
 * owns ANY motion. Every show* call resolves a style (preset sizes/colors
 * unchanged) plus a motion verb and delegates to the ONE executor,
 * `spawnFloatingDamageText` — the same verb engine that renders the
 * server-confirmed numbers. Consequences, all deliberate:
 *
 *  - depth 50 → Z_LAYERS.FLOATING_DAMAGE_TEXT (executor-owned) — the
 *    two-depth split between beat texts and confirmed numbers is closed;
 *  - the `bounce` yoyo flash and the `fade` start-delay are retired —
 *    verbs are monotone-fade with at most one overshoot (spec §3.4);
 *  - reduced-motion / cap / density degradation ride the executor's gates.
 *
 * Damage/heal numbers resolve the full grammar (resolveFctStyle): tier
 * sizes, school-tint delegation, `+` heal prefix — one visual language.
 */
export class FloatingTextManager {
  private scene: unknown;
  private particleManager: EnhancedParticleManager;

  constructor(scene: unknown) {
    this.scene = scene;
    this.particleManager = EnhancedParticleManager.getInstance(scene);
  }

  /**
   * Set the scene reference
   */
  public setScene(scene: unknown): void {
    this.scene = scene;
    this.particleManager = EnhancedParticleManager.getInstance(scene);
  }

  /**
   * Show floating text at position. Style presets resolve font size + color
   * exactly as before (byte-identical values); motion delegates to the verb
   * executor. `config.verb` overrides the preset-derived verb.
   */
  public showFloatingText(text: string, x: number, y: number, config: StyledFloatingTextConfig): void {
    // Resolve style preset → fontSize + color (decimal int). Unchanged table.
    let fontSize = parseInt(String(config.fontSize), 10) || 18;
    let color = parseHexColor(config.color);
    switch (config.style) {
      case 'damage':   fontSize = 24; break;
      case 'heal':     fontSize = 22; break;
      case 'critical': fontSize = 28; break;
      case 'ability':  fontSize = 18; break;
    }
    // Style color via the colorblind-safe-aware resolver (W3.1). Normal mode is
    // byte-identical; null = a style the manager colors itself (info/xp) → keep config.color.
    const styleColor = resolveFloatingTextColor(config.style, getMotionPrefs().colorblindSafe);
    if (styleColor !== null) color = styleColor;

    const verb: FctMotionVerb =
      config.verb ?? FCT_PRESET_STYLE_VERBS[config.style ?? 'info'] ?? 'drift';

    spawnFloatingDamageText(this.scene, {
      x,
      y: y - config.offsetY,
      text,
      color,
      fontSize,
      durationMs: config.duration,
      verb,
      tier: config.tier ?? 1,
      targetKey: config.targetKey,
      // Beat texts are special beats: never dropped by the cap/degrade
      // gates unless the caller opts in (spec §3.2).
      alwaysRender: config.alwaysRender ?? true,
      companion: config.companion,
    });
  }

  /**
   * Show damage number — full FCT grammar (tier size/verb/duration, school
   * glyph via the resolver; beat numbers are local predictions ⇒ physical).
   */
  public showDamageNumber(
    amount: number, x: number, y: number, isCritical: boolean = false,
    opts?: { tick?: boolean; targetKey?: string },
  ): void {
    const style = resolveFctStyle({
      amount,
      school: 'physical',
      flags: { crit: isCritical, tick: opts?.tick, local: true },
      colorblindSafe: getMotionPrefs().colorblindSafe,
    });
    this.showFloatingText(style.text, x, y, {
      text: style.text,
      color: style.color,
      fontSize: `${style.fontSize}px`,
      duration: style.durationMs,
      offsetY: 20,
      verb: style.verb,
      tier: style.tier,
      targetKey: opts?.targetKey,
      alwaysRender: style.alwaysRender,
    });

    // Spawn particle effect (one-path extra, unchanged by the migration).
    this.particleManager.spawnDamageEffect(amount, x, y, isCritical);
  }

  /**
   * Show healing number — softRise verb + `+` prefix via the resolver.
   */
  public showHealingNumber(amount: number, x: number, y: number): void {
    const style = resolveFctStyle({
      amount,
      school: 'physical',
      flags: { heal: true, local: true },
      colorblindSafe: getMotionPrefs().colorblindSafe,
    });
    this.showFloatingText(style.text, x, y, {
      text: style.text,
      color: style.color,
      fontSize: `${style.fontSize}px`,
      duration: style.durationMs,
      offsetY: 20,
      verb: style.verb,
      tier: style.tier,
      alwaysRender: true,
    });

    this.particleManager.spawnHealingEffect(x, y, amount);
  }

  /**
   * Show ability name — copy unchanged; motion is the drift verb.
   */
  public showAbilityName(abilityName: string, x: number, y: number): void {
    this.showFloatingText(abilityName, x, y, {
      text: abilityName,
      color: '#aa00ff',
      fontSize: '18px',
      duration: 1500,
      offsetY: 40,
      style: 'ability',
    });
  }

  /**
   * Show XP gain — copy unchanged; motion is the drift verb.
   */
  public showXPGain(amount: number, x: number, y: number): void {
    this.showFloatingText(`+${amount} XP`, x, y, {
      text: `+${amount} XP`,
      color: '#3498db',
      fontSize: '20px',
      duration: 1500,
      offsetY: 30,
      style: 'xp',
    });
  }

  /**
   * Per-frame housekeeping — intentional no-op. The executor owns every
   * label's lifecycle (self-destroying rAF loops).
   */
  public update(_time: number, _delta: number): void {
    // Intentional no-op.
  }

  /**
   * Clean up — kept for API compatibility. Labels are executor-owned and
   * self-destroy within their verb duration (≤1.5s), so there is nothing
   * to tear down here anymore.
   */
  public cleanup(): void {
    // Intentional no-op (executor-owned lifecycle).
  }
}
